import { StyleSheet, css } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faFileImage, faQuestionCircle, faExpand } from '@fortawesome/free-solid-svg-icons'
import fileDownload from 'js-file-download'
import {json2csv} from 'json-2-csv'
import domtoimage from 'dom-to-image';

import {
         Bar,
         BarChart,
         XAxis,
         YAxis,
         Tooltip,
         CartesianGrid,
         ResponsiveContainer,
         Cell,
         Legend
         } from 'recharts';
import { ChartItem } from 'src/models/ChartItem.js';
import * as React from 'react';
import { HorizontalAxisTick, VerticalAxisTick } from './AxisTick';
import { Popover, PopoverBody, Card, CardHeader, CardBody } from 'reactstrap';
import { RequestState } from 'src/models/RequestState';
import { CustomToolTip } from './CustomTooltip';
import { StackedChartTooltip } from './StackedChartTooltip';
import { StackedChartItem } from 'src/models/StackedChartItem';
import { JsonConvert } from 'json2typescript';
import { AbbreviationItem } from 'src/models/AbbreviationItem';

interface IStackedHorizontalBarChartProps {
    id: string,
    title: string,
    tooltip: string,
    state?: RequestState,
    error?: string,
    data: StackedChartItem[],
    customViews?: any,
    yAxisWidth?: number,
    onExpandClick?: any,
    value1Label?: string,
    value2Label?: string,
    value3Label?: string,
    value4Label?: string,
    labelName?: string,
    valueFormatter?: any,
    onItemClick?: any
    tickCutOff?: number;
    tickFormatter?: (value:string)=>{}
}

class StackedHorizontalBarChartState {
    public readonly isPopupOpen: boolean;
    constructor() {
        this.isPopupOpen = false;
    }
}

const value1Fill = "#ed7250";
const value2Fill = "#e5cf60";
const value3Fill = "#a608bf";
const value4Fill = "#0967bf";

export class StackedHorizontalBarChart extends React.Component<IStackedHorizontalBarChartProps,StackedHorizontalBarChartState> {
   
    public static defaultProps = {
        yAxisWidth: 190,
        state: RequestState.SUCCESS,
        error: "",
        value1Label: "Count",
        labelName: "Label",
        onItemClick: (chartItem: any)=>{},
        tickCutOff: 30
    };

    constructor(props: IStackedHorizontalBarChartProps){
       super(props)
       this.state = new StackedHorizontalBarChartState(); 
    }

    public render() {

        let content;

        const data = this.props.data;

        if(this.props.state === RequestState.SUCCESS){
            content = (
                <ResponsiveContainer  height='100%' width='100%' className={css(styles.chart_background)} >
                        <BarChart layout="vertical" data={data}
                                margin={{top: 5, right: 30, left: 5, bottom: 5}}
                                barGap={0}
                                >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false}/> 
                            <XAxis type="number" axisLine={false} tick={<HorizontalAxisTick fontSize={12} tickFormatter={this.props.tickFormatter} />} interval={1} allowDecimals={false}/>
                            <YAxis type="category" dataKey="name" axisLine={false} hide={false} interval={0} width={this.props.yAxisWidth} 
                                    tickFormatter={this.tickFormatter} tick={{fontSize:11}}/>} 
                            />
                            <Legend />
                            <Tooltip 
                                cursor={false}
                                content={<StackedChartTooltip 
                                                labelName={this.props.labelName}
                                                value1Label={this.props.value1Label}
                                                value2Label = {this.props.value2Label}
                                                value3Label = {this.props.value3Label}
                                                value4Label = {this.props.value4Label}
                                                valueFormatter={this.props.valueFormatter}
                                                />} />     
                                                                                                    
                            <Bar name={this.props.value1Label} dataKey="amount1" barSize={5} stackId="1" onClick={this.onBarClick} fill = {value1Fill} >
                                {
                                    data.map(this.buildHorizontalBarValue)
                                }
                            </Bar>
                            <Bar name = {this.props.value2Label} dataKey="amount2" barSize={5} stackId="2" onClick={this.onBarClick} fill = {value2Fill} >
                                {
                                    data.map(this.buildHorizontalBarValue)
                                }
                            </Bar>
                            <Bar name={this.props.value3Label} dataKey="amount3" barSize={5} stackId="3" onClick={this.onBarClick} fill = {value3Fill} >
                                {
                                    data.map(this.buildHorizontalBarValue)
                                }
                            </Bar>
                            <Bar name={this.props.value4Label} dataKey="amount4" barSize={5} stackId="4" onClick={this.onBarClick} fill = {value4Fill} >
                                {
                                    data.map(this.buildHorizontalBarValue)
                                }
                            </Bar>
                        </BarChart>
                </ResponsiveContainer>   
            );
        }else if(this.props.state === RequestState.LOADING){
            content = (<div className={css(styles.empty_view)} >
                            Loading
                        </div>);
        }else if(this.props.state === RequestState.ERROR){
            content = (<div className={css(styles.empty_view)} >
                            {this.props.error}
                        </div>);
        }else{
            content = (<div className={css(styles.empty_view)} >
                            Not requested
                        </div>);
        }

        return (
        
        <Card id={this.props.id} className={css(styles.card)} >
           
           <CardHeader className={css(styles.card_header)} >
                <div className={css(styles.label_container)}>
                        <div className={css(styles.card_label)}>
                            {this.props.title}
                        </div>

                        <div id={"ic_help" + this.props.id} onClick={this.onHelpClick} className={css(styles.icon)} >
                                <FontAwesomeIcon icon={faQuestionCircle} />
                        </div>

                        <Popover placement="bottom"
                                isOpen={this.state.isPopupOpen}
                                target={"ic_help" + this.props.id}
                                trigger="focus"
                                toggle={this.toggle} >
                            <PopoverBody>{this.props.tooltip}</PopoverBody>
                        </Popover>

                    </div>
                                    
                    <div id="custom_views" className={css(styles.customViews)} >
                        {this.props.customViews}
                    </div>

                    <div id="icons" className={css(styles.icons)} >
                        <div onClick={this.onDownloadImageClick} className={css(styles.icon)} title={"Save as image"} >
                            <FontAwesomeIcon icon={faFileImage} />
                        </div>

                        <div onClick={this.onDownloadDataClick} className={css(styles.icon)} title={"Download data"} >
                            <FontAwesomeIcon icon={faDownload} />
                        </div>

                        <div className={css(styles.icon)} title={"Expand chart"} onClick={this.onExpandClick} >
                            <FontAwesomeIcon icon={faExpand} />
                        </div>
                     </div>      
           </CardHeader>
                                 
            <CardBody id="chart_container" className={css(styles.chart_container)} >
                {content}         
            </CardBody>    
            
            
        </Card>);
    }

    private tickFormatter = (value: string) => {
        try{
            const value_parsed = JSON.parse(value)
            const jsonConvert = new JsonConvert();
            const abbr_item = jsonConvert.deserialize(value_parsed, AbbreviationItem);
            return abbr_item.name_abbr;
        }catch(error){
            return value;
        }
    }

    private onBarClick = (entry: ChartItem, index: number) => {
        if(this.props.onItemClick !== undefined) {
            this.props.onItemClick(entry.payload);
        }
    }

    private buildHorizontalBarValue = (entry: any,index: number) => {
        return( 
            <Cell cursor="pointer" key={`cell-${index}`}/>
        );
    }
    
    private onDownloadImageClick = (event: any) => {
        const element = document.getElementById(this.props.id);
        if(element !== null){
            domtoimage.toBlob(element)
            .then((blob: any) => {
                fileDownload(blob, `${this.props.id}.png`);
            });
        }
    }

    private onDownloadDataClick = (event: any) => {
        json2csv(this.props.data,(err: any,csv: any) => {
            fileDownload(csv,"data.csv")
        })
    }

    private onHelpClick = (event: any) => {
        this.setState({
            ...this.state, isPopupOpen: !this.state.isPopupOpen
        })
    }

    private toggle = () => {
        this.setState({
            ...this.state, isPopupOpen: !this.state.isPopupOpen
        })
    }

    private onExpandClick = () => {
        if(this.props.onExpandClick !== undefined){
            this.props.onExpandClick();
        }
    }    
}



const styles = StyleSheet.create({
    card : {
        display: "grid",
        gridTemplateRows: "min-content auto",
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
        transition: "all 0.3s cubic-bezier(.25,.8,.25,1)",
        ":hover": {
            boxShadow: "0 10px 20px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)"
        }
    },

    card_header : {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        padding: 0,
    },

    label_container: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center"
    },
        
    card_label: {
        marginLeft: 25,
        marginRight: 10,
        marginTop: 10,
        marginBottom: 10,
        fontSize: 16,
        color: "#5a5a5aff"
    },

    customViews : {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginLeft: "auto",
        marginRight: 20,
    },

    icons : {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginLeft: 10,
        marginRight: 10,
    },

    icon: {
        marginLeft: 10,
        marginRight: 10,
        color: "#5a5a5aff",
        ":hover": {
            cursor: "pointer",
            color: "#006bcfff"
        }
    },

    seperator: {
        backgroundColor: "#d2d2d2ff",
        height: 1
    },
    
    empty_view: {
        padding: 100,
    },

    chart_container: {
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        height: 320,
        width: "100%",
        marginTop: 10,
        marginLeft: "auto",
        marginRight: "auto",
        alignItems: "center",
        padding: "0px"
    },

    chart_background: {
        backgroundColor: "white"
    }

})

export default StackedHorizontalBarChart;


// WEBPACK FOOTER //
// ./src/components/charts/StackedHorizontalBarChart.tsx